/// <reference path="../../types/global.d.ts" />
/**
 * i18next integration (new)
 * i18next integration for MATCHi frontend apps.
 */
import i18next from "i18next";
import { initReactI18next, useTranslation, Trans } from "react-i18next";

i18next.use(initReactI18next).init({
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
    prefix: "{",
    suffix: "}",
  },
  resources: {},
  keySeparator: false,
  nsSeparator: false,
  react: {
    useSuspense: false,
  },
});

export { useTranslation, Trans };
export type { TFunction } from "i18next";

/**
 * Standalone translation function for code that cannot use React hooks.
 *
 * Use `useTranslation()` in React function components — it's reactive and
 * re-renders on language change. This exists only for contexts where hooks
 * are impossible: VideoJS component classes, plain utility functions, and
 * callbacks that run outside the React tree.
 *
 * Named `tGlobal` (not `t`) to make call sites visibly different from the
 * hook-based `t` so reviewers can tell at a glance whether a file is using
 * the reactive or the global variant.
 *
 * Works after initLocales() has been called (i.e. after _app.js runs).
 */
export const tGlobal = i18next.t.bind(i18next);

/**
 * Change the current i18next locale.
 * Use this in language pickers.
 */
export async function changeLanguage(locale: I18nLocale): Promise<void> {
  await i18next.changeLanguage(locale);
}

import type { AppNamespace } from "./i18next-server";

interface LocalesInitOptions {
  locale: I18nLocale;
  namespace: AppNamespace;
  resources: Partial<Record<I18nLocale, Record<string, string>>>;
}

/**
 * Initialize i18next for an app.
 *
 * @example
 * import { initLocales, useTranslation } from "i18n";
 * import en from "../locales/checkout/en.json";
 * import sv from "../locales/checkout/sv.json";
 *
 * await initLocales({
 *   locale: "sv",
 *   namespace: "checkout",
 *   resources: { en, sv }
 * });
 *
 * // In components:
 * const { t } = useTranslation();
 * return <p>{t("Hello")}</p>;
 */
export async function initLocales({
  locale,
  namespace,
  resources,
}: LocalesInitOptions): Promise<void> {
  // namespace parameter ensures type-safe app identification
  void namespace;

  for (const [lang, translations] of Object.entries(resources)) {
    i18next.addResourceBundle(lang, "translation", translations, true, true);
  }

  if (i18next.language !== locale) {
    await i18next.changeLanguage(locale);
  }
}
